'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { t } from '@/i18n';
import axios from 'axios';
import { 
  Zap, ShieldCheck, Gamepad2, Layers, 
  Clock, ShoppingCart, Loader2, AlertCircle, Minus, Plus
} from 'lucide-react';

import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import FloatingCart from '../../components/FloatingCart';
import PlayerVerifyModal from '../../components/PlayerVerifyModal';

import dynamic from 'next/dynamic';
import SanitizedHTML from '@/components/SanitizedHTML';

const CartSidebar = dynamic(() => import('@/components/landing/CartSidebar'), { ssr: false });
const PaymentModal = dynamic(() => import('@/components/landing/PaymentModal'), { ssr: false });
const LoginModal = dynamic(() => import('../../components/LoginModal'), { ssr: false });
const MobileNumberModal = dynamic(() => import('../../components/MobileNumberModal'), { ssr: false });
const QrCodeModal = dynamic(() => import('../../components/QrCodeModal'), { ssr: false });

import { useCart } from '@/hooks/useCart';
import { useCartContext } from '@/components/CartProvider';
import { useAppAuth } from '@/hooks/useAppAuth';
import { useStockEngine } from '@/hooks/useStockEngine';

interface ProductPageClientProps {
  product: any;
}

export default function ProductPageClient({ product }: ProductPageClientProps) {
  const auth = useAppAuth();
  const API_BASE = '/api';

  const {
    cart,
    addToCart,
    clearCart,
    updateQty,
    removeFromCart,
    uid,
    setUid,
    validationResult,
    setValidationResult,
    selectedTabRegion,
    products,
    stockAvailability,
    regionPools,
    charges,
    paymentConfig,
    loadingCatalog,
    activeOffers,
    hydrateCatalog,
    mergeProducts,
  } = useCartContext();

  const [currentProduct, setCurrentProduct] = useState(product);
  const [localOffers, setLocalOffers] = useState<Record<string, any>>({});

  useEffect(() => {
    let isMounted = true;
    if (typeof mergeProducts === 'function') {
      mergeProducts([product]);
    }

    const fetchFreshData = async () => {
      try {
        const [productRes, offersRes, chargesRes, paymentRes] = await Promise.allSettled([
          axios.get(`${API_BASE}/public/products/${product.slug || product.id}`),
          axios.get(`${API_BASE}/public/active-offers`),
          axios.get(`${API_BASE}/public/charges`),
          axios.get(`${API_BASE}/public/payment-config`),
        ]);

        if (!isMounted) return;

        if (productRes.status === 'fulfilled') {
          const freshProd = productRes.value.data?.data || productRes.value.data;
          if (freshProd?.id) {
            setCurrentProduct(freshProd);
            if (typeof mergeProducts === 'function') {
              mergeProducts([freshProd]);
            }
          }
        }

        let offerMap: Record<string, any> = {};
        if (offersRes.status === 'fulfilled') {
          const fetchedOffers = offersRes.value.data?.data || offersRes.value.data || [];
          if (Array.isArray(fetchedOffers)) {
            for (const offer of fetchedOffers) {
              if (Array.isArray(offer.products)) {
                for (const op of offer.products) {
                  offerMap[op.productId] = {
                    ...op,
                    offerName: offer.name,
                    endTime: offer.endTime,
                  };
                }
              }
            }
          }
          setLocalOffers(offerMap);
        }

        const hydrationPayload: any = {};
        if (Object.keys(offerMap).length > 0) {
          hydrationPayload.activeOffers = offerMap;
        }
        if (chargesRes.status === 'fulfilled') {
          hydrationPayload.charges = chargesRes.value.data?.data || chargesRes.value.data;
        }
        if (paymentRes.status === 'fulfilled') {
          hydrationPayload.paymentConfig = paymentRes.value.data?.data || paymentRes.value.data;
        }

        if (Object.keys(hydrationPayload).length > 0 && typeof hydrateCatalog === 'function') {
          hydrateCatalog(hydrationPayload);
        }
      } catch (e) {
        console.error('Error fetching fresh product/offer data:', e);
      }
    };

    fetchFreshData();

    return () => {
      isMounted = false;
    };
  }, [product.id, product.slug, mergeProducts, hydrateCatalog]);

  const cartHook = useCart(products, stockAvailability, 0, false, charges, activeOffers);
  const playerRegion = selectedTabRegion;
  
  const stockEngine = useStockEngine(
    cart,
    stockAvailability,
    regionPools,
    playerRegion,
  );

  // local client UI states
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  
  // Pending actions
  const [openLoginPendingCart, setOpenLoginPendingCart] = useState(false);
  const [pendingCartAction, setPendingCartAction] = useState<'add' | 'buy'>('add');

  // Checkout flows states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [pendingGenieOrder, setPendingGenieOrder] = useState<any>(null);
  // Read playerId and zoneId from URL if navigated from game catalog
  const searchParams = useSearchParams();
  const urlPlayerId = searchParams?.get('playerId') || null;
  const urlZoneId = searchParams?.get('zoneId') || null;
  const urlRegion = searchParams?.get('region') || null;

  useEffect(() => {
    if (urlPlayerId && (!uid || uid !== urlPlayerId)) {
      setUid(urlPlayerId);
      if (!validationResult || validationResult.uid !== urlPlayerId) {
        setValidationResult({
          valid: true,
          nickname: urlPlayerId,
          uid: urlPlayerId,
          player_id: urlPlayerId,
          zone_id: urlZoneId || undefined,
          zoneId: urlZoneId || undefined,
          region: urlRegion || selectedTabRegion || 'SG',
        });
      }
    }
  }, [urlPlayerId, urlZoneId, urlRegion, uid, validationResult, setUid, setValidationResult, selectedTabRegion]);

  // Safe product details fallbacks
  const game = currentProduct.game || 'Free Fire';
  const category = currentProduct.category || 'diamonds';
  const delivery = currentProduct.delivery || 'Instant';
  const defaultRegion = currentProduct.defaultRegion || 'LK';

  // Check for active promotional offer
  const currentOffer =
    activeOffers?.[currentProduct.id] ||
    localOffers?.[currentProduct.id] ||
    currentProduct.activeOffer;
  const hasActiveOffer = Boolean(currentOffer && currentOffer.discountedPrice !== undefined);

  // Format price
  const baseDisplayPrice = currentProduct.websitePriceLkr ?? currentProduct.priceLkr ?? 0;
  const displayPrice = hasActiveOffer
    ? Number(currentOffer.discountedPrice)
    : baseDisplayPrice;

  const originalPrice = hasActiveOffer
    ? Number(currentOffer.originalPrice || baseDisplayPrice)
    : (currentProduct.visualDiscountEnabled && currentProduct.visualDiscountPercentage > 0
        ? baseDisplayPrice * (1 + currentProduct.visualDiscountPercentage / 100)
        : null);

  const discountPercentage = hasActiveOffer
    ? (originalPrice && originalPrice > displayPrice
        ? Math.round(((originalPrice - displayPrice) / originalPrice) * 100)
        : currentOffer.discountPercentage || null)
    : (currentProduct.visualDiscountEnabled && currentProduct.visualDiscountPercentage > 0
        ? currentProduct.visualDiscountPercentage
        : null);

  // Real-time stock status
  const stockInfo = stockAvailability[currentProduct.id];
  const isOutOfStock = stockInfo ? !stockInfo.isAvailable : false;

  const performCartAction = useCallback((action: 'add' | 'buy') => {
    addToCart(currentProduct.id, {
      qty: quantity,
      item_id: currentProduct.id,
      isChannelItem: false,
    });

    if (action === 'buy') {
      setIsCartOpen(true);
    } else {
      setIsCartOpen(true);
    }
  }, [currentProduct.id, addToCart, quantity]);

  const hasZoneInput = Boolean(
    currentProduct.hasZoneInput ||
    (Array.isArray(currentProduct.inputForm) && currentProduct.inputForm.length > 1) ||
    /mobile-legends|mlbb|genshin/i.test(currentProduct.game || '') ||
    /mobile-legends|mlbb|genshin/i.test(currentProduct.slug || '') ||
    /mobile legends|mlbb/i.test(currentProduct.name || '')
  );
  const playerLabel =
    currentProduct.playerLabel || (/genshin/i.test(currentProduct.game || '') ? 'UID' : 'Player ID');
  const zoneLabel =
    currentProduct.zoneLabel || (/genshin/i.test(currentProduct.game || '') ? 'Server' : 'Zone ID');

  const effectiveZoneId =
    validationResult?.zoneId ||
    validationResult?.zone_id ||
    urlZoneId ||
    '';

  const targetProductRegion = (
    currentProduct.defaultRegion ||
    (currentProduct.slug?.includes('indonesia') ? 'ID' : '')
  ).toString().trim().toUpperCase();

  const isRegionMatching = !targetProductRegion || !validationResult?.region || (
    targetProductRegion === 'ID'
      ? validationResult.region.toUpperCase() === 'ID'
      : validationResult.region.toUpperCase() !== 'ID'
  );

  const isGameMatching = !validationResult?.game || (
    /free\s*fire|freefire/i.test(game)
      ? /free\s*fire|freefire/i.test(validationResult.game)
      : validationResult.game.toLowerCase() === game.toLowerCase()
  );

  const isVerifiedForThisProduct = Boolean(
    validationResult &&
    (validationResult.valid || validationResult.nickname) &&
    (!hasZoneInput || Boolean(effectiveZoneId)) &&
    isRegionMatching &&
    isGameMatching
  );

  const handleCartClick = (action: 'add' | 'buy') => {
    if (isOutOfStock) return;
    setPendingCartAction(action);

    if (!auth.isAuthenticated) {
      setOpenLoginPendingCart(true);
      setIsLoginModalOpen(true);
      return;
    }

    if (!isVerifiedForThisProduct) {
      setIsPlayerModalOpen(true);
      return;
    }

    performCartAction(action);
  };

  const handleLoginSuccess = () => {
    auth.refreshSession();
    setIsLoginModalOpen(false);
    
    if (openLoginPendingCart) {
      setOpenLoginPendingCart(false);
      if (!isVerifiedForThisProduct) {
        setIsPlayerModalOpen(true);
      } else {
        performCartAction(pendingCartAction);
      }
    }
  };

  const handleLoginClose = () => {
    setIsLoginModalOpen(false);
    if (openLoginPendingCart) {
      setOpenLoginPendingCart(false);
      // "if doesn't login open player verify modal"
      setIsPlayerModalOpen(true);
    }
  };

  const handlePlayerVerifySuccess = (verifiedUid: string, nickname: string) => {
    setIsPlayerModalOpen(false);
    performCartAction(pendingCartAction);
  };

  // Checkout implementations
  const buildOrderDetails = useCallback(() => {
    const items = Object.entries(cart).map(([id, item]) => ({
      itemId: id,
      quantity: item.qty,
    }));

    const effectiveZoneId =
      validationResult?.zoneId ||
      validationResult?.zone_id ||
      urlZoneId ||
      undefined;

    return {
      playerId: validationResult?.userId || validationResult?.uid || uid || urlPlayerId,
      region: validationResult?.region || selectedTabRegion || 'SG',
      playerName: validationResult?.nickname || '',
      ...(effectiveZoneId ? { zoneId: effectiveZoneId } : {}),
      items,
      ...(auth.email ? { userEmail: auth.email } : {}),
      ...(auth.firebaseUser
        ? { firebaseUid: auth.firebaseUser.uid }
        : {}),
    };
  }, [cart, validationResult, uid, auth]);

  const handleGenieCheckout = async (mode: 'CARD' | 'QR' = 'CARD') => {
    setIsSubmittingOrder(true);
    try {
      const orderDetails = buildOrderDetails();
      const resp = await axios.post(`${API_BASE}/payment/public-order`, {
        gateway: 'GENIE',
        orderDetails: {
          ...orderDetails,
          paymentMethod: mode === 'QR' ? 'QR' : undefined,
        },
      });
      const data = resp.data.data || resp.data;

      if (data.paymentUrl || data.qrCode) {
        if (mode === 'CARD' && data.paymentUrl) {
          window.location.href = data.paymentUrl;
        } else {
          setPendingGenieOrder(data);
          setShowPaymentModal(false);
        }
      }
    } catch (e: any) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to initialize payment');
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handleBcoinCheckout = async () => {
    setIsSubmittingOrder(true);
    try {
      const orderDetails = buildOrderDetails();
      const token = await auth.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await axios.post(
        `${API_BASE}/payment/public-order-with-bcoins`,
        { orderDetails },
        { headers }
      );
      const data = resp.data.data || resp.data;
      const statusId =
        data?.id ??
        data?.transactionId ??
        data?.batchId ??
        data?.orderBatchId ??
        null;

      if (!statusId) {
        throw new Error('Order placed but missing payment status id');
      }

      clearCart();
      setIsCartOpen(false);
      setShowPaymentModal(false);
      window.location.href = `/payment-status?id=${encodeURIComponent(String(statusId))}`;
    } catch (e: any) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to initialize BCoins payment');
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handleEzCashCheckout = async (referenceNumber: string) => {
    const token = await auth.getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const resp = await axios.post(
      `${API_BASE}/payment/public-order-with-ezcash`,
      { orderDetails: buildOrderDetails(), referenceNumber },
      { headers },
    );
    return resp.data.data || resp.data;
  };



  const handleCheckout = () => {
    if (!auth.isAuthenticated) {
      setIsLoginModalOpen(true);
      return;
    }

    if (auth.hasMobile === false) {
      setIsMobileModalOpen(true);
      return;
    }

    setSelectedPaymentMethod(null);
    setShowPaymentModal(true);
  };

  const gameSlug =
    currentProduct.gameSlug ||
    (/free\s*fire|freefire/i.test(game)
      ? 'free-fire'
      : /pubg/i.test(game)
        ? 'pubg-mobile'
        : /mobile\s*legends|mlbb/i.test(game)
          ? 'mobile-legends'
          : /genshin/i.test(game)
            ? 'genshin-impact'
            : game.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'free-fire');

  const isIndonesia =
    (currentProduct.defaultRegion || '').toUpperCase() === 'ID' ||
    Boolean(currentProduct.slug?.includes('indonesia'));
  const gameUrl = isIndonesia
    ? `/games/${gameSlug}?region=indonesia`
    : `/games/${gameSlug}`;

  const categoryDetails = (() => {
    const cat = (category || '').toLowerCase().trim();
    if (cat === 'membership' || cat === 'memberships') {
      return { label: 'Memberships', hash: 'subscriptions' };
    }
    if (cat === 'weekly_pass' || cat === 'subscriptions' || cat === 'subscription') {
      return { label: 'Subscriptions', hash: 'subscriptions' };
    }
    if (cat === 'diamonds' || cat === 'diamond') {
      return { label: 'Diamonds', hash: 'diamonds' };
    }
    if (cat === 'offers' || cat === 'offer' || cat === 'special_offers') {
      return { label: 'Special Offers', hash: 'offers' };
    }
    if (cat === 'evos' || cat === 'evo') {
      return { label: 'Evo Access', hash: 'evo-access' };
    }
    if (cat === 'level_ups' || cat === 'level_up') {
      return { label: 'Level Up Pass', hash: 'level-up-pass' };
    }
    return {
      label: cat ? cat.charAt(0).toUpperCase() + cat.slice(1) : 'Packages',
      hash: '',
    };
  })();

  const categoryUrl = categoryDetails.hash
    ? `${gameUrl}#${categoryDetails.hash}`
    : gameUrl;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col font-sans">
      <Navbar />

      <main className="flex-grow pt-28 pb-16 px-4 md:px-8 max-w-7xl mx-auto w-full">
        {/* Breadcrumbs */}
        <nav
          aria-label="Breadcrumb"
          className="mb-6 flex flex-wrap items-center gap-2 text-[10px] font-mono font-bold uppercase tracking-widest text-slate-400"
        >
          <Link href="/" className="hover:text-[#5A98D8] transition-colors">
            Home
          </Link>
          <span className="text-slate-300" aria-hidden="true">/</span>
          <Link href={gameUrl} className="hover:text-[#5A98D8] transition-colors">
            {game}
          </Link>
          <span className="text-slate-300" aria-hidden="true">/</span>
          <Link href={categoryUrl} className="hover:text-[#5A98D8] transition-colors">
            {categoryDetails.label}
          </Link>
          <span className="text-slate-300" aria-hidden="true">/</span>
          <span className="text-[#5A98D8]" aria-current="page">
            {currentProduct.name}
          </span>
        </nav>

        {/* Product Page Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left Column: Image Card & Quick Specs */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="relative aspect-square w-full border border-border bg-card p-8 flex items-center justify-center overflow-hidden group hover:shadow-md transition-all duration-300">
              <div className="absolute top-3 left-3 z-10 bg-accent text-accent-foreground text-[9px] font-mono font-bold tracking-widest px-2.5 py-1 flex items-center gap-1.5 rounded-[2px]">
                <Zap size={10} className="fill-current" />
                {delivery === 'Instant' ? 'INSTANT (1S) FASTEST DELIVERY IN SRI LANKA' : `${delivery.toUpperCase()} DELIVERY`}
              </div>

              {hasActiveOffer ? (
                <div className="absolute top-3 right-3 z-10 bg-gradient-to-br from-blue-600 to-blue-800 text-white text-[9px] font-mono font-black px-2 py-1.5 rounded-[2px] shadow-lg flex flex-col items-center leading-none">
                  {discountPercentage ? (
                    <>
                      <span className="mb-0.5">{discountPercentage}%</span>
                      <span className="text-[6px] opacity-90 uppercase tracking-tighter">OFF</span>
                    </>
                  ) : (
                    <>
                      <span className="mb-0.5 text-[8px] leading-tight tracking-wider">LIMITED</span>
                      <span className="text-[6px] uppercase tracking-[0.15em] font-bold text-blue-200">OFFER</span>
                    </>
                  )}
                </div>
              ) : (
                currentProduct.visualDiscountEnabled && currentProduct.visualDiscountPercentage > 0 && (
                  <div className="absolute top-3 right-3 z-10 bg-red-600 text-white text-[9px] font-mono font-black px-2 py-1 rounded-[2px] flex flex-col items-center leading-none">
                    <span className="mb-0.5">{currentProduct.visualDiscountPercentage}%</span>
                    <span className="text-[6px] opacity-90 uppercase tracking-tighter">OFF</span>
                  </div>
                )
              )}

              <div className="relative w-4/5 h-4/5">
                <Image
                  src={
                    currentProduct.imageUrl
                      ? currentProduct.imageUrl.startsWith('http')
                        ? currentProduct.imageUrl
                        : currentProduct.imageUrl
                      : 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23888888"><path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>'
                  }
                  alt={`${currentProduct.name} - ${game} Topup Sri Lanka | Instant 1s Fastest Delivery`}
                  fill
                  unoptimized
                  priority
                  className="object-contain group-hover:scale-105 transition-transform duration-500 p-2"
                />
              </div>
            </div>

            {/* Quick Specs List */}
            <div className="border border-border bg-card p-5 flex flex-col gap-4">
              <h3 className="text-[10px] font-mono font-bold tracking-widest text-muted-foreground uppercase border-b border-border pb-3">
                Product Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-[9px] font-mono text-muted-foreground block uppercase tracking-widest mb-0.5">Game</span>
                  <span className="font-mono text-[11px] font-bold uppercase text-[#1a5b9b] flex items-center gap-1.5">
                    <Gamepad2 size={12} className="text-[#5A98D8] shrink-0" />
                    {game}
                  </span>
                </div>
                <div>
                  <span className="text-[9px] font-mono text-muted-foreground block uppercase tracking-widest mb-0.5">Category</span>
                  <span className="font-mono text-[11px] font-bold uppercase text-[#1a5b9b] flex items-center gap-1.5">
                    <Layers size={12} className="text-[#5A98D8] shrink-0" />
                    {categoryDetails.label}
                  </span>
                </div>
                <div>
                  <span className="text-[9px] font-mono text-muted-foreground block uppercase tracking-widest mb-0.5">Delivery</span>
                  <span className="font-mono text-[11px] font-bold uppercase text-[#1a5b9b] flex items-center gap-1.5">
                    <Clock size={12} className="text-[#5A98D8] shrink-0" />
                    {delivery === 'Instant' ? 'Instant (1s) Fastest Delivery in Sri Lanka' : '10-30 mins'}
                  </span>
                </div>
                <div>
                  <span className="text-[9px] font-mono text-muted-foreground block uppercase tracking-widest mb-0.5">Region</span>
                  <span className="font-mono text-[11px] font-bold uppercase text-[#1a5b9b] flex items-center gap-1.5">
                    <ShieldCheck size={12} className="text-[#5A98D8] shrink-0" />
                    {defaultRegion}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-7 flex flex-col gap-5 min-w-0">
            <div className="flex flex-wrap gap-1.5">
              <span className="bg-accent text-accent-foreground text-[9px] font-mono uppercase px-2 py-0.5 rounded-[2px] tracking-widest font-bold">
                {game}
              </span>
              <span className="bg-muted text-muted-foreground text-[9px] font-mono uppercase px-2 py-0.5 rounded-[2px] tracking-widest font-bold border border-border">
                {categoryDetails.label}
              </span>
              <span className="bg-muted text-muted-foreground text-[9px] font-mono uppercase px-2 py-0.5 rounded-[2px] tracking-widest font-bold border border-border">
                {defaultRegion} Region
              </span>
            </div>

            <div>
              <h1 className="text-4xl md:text-5xl font-display uppercase leading-[0.92] tracking-tight text-foreground mb-3">
                {currentProduct.seoTitle || `${currentProduct.name} ${game} Sri Lanka | Baasil Topup`}
              </h1>
              <p className="text-muted-foreground text-[11px] font-mono uppercase tracking-[0.15em] max-w-xl leading-relaxed">
                {currentProduct.metaDescription || `Get your ${currentProduct.name} package instantly in Sri Lanka. Cheap, secure, and fast.`}
              </p>
            </div>

            {/* Price & Checkout Box */}
            <div className="border border-border bg-card p-6 md:p-8">
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] font-mono text-muted-foreground block uppercase tracking-widest font-bold">
                    Special Web Price
                  </span>
                  {hasActiveOffer && (
                    <span className="text-[8px] font-mono font-extrabold uppercase tracking-wider text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-[2px]">
                      Limited Time Offer
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-4xl md:text-5xl uppercase leading-none" style={{ color: '#1a5b9b' }}>
                    Rs.{(displayPrice * quantity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {originalPrice && (
                    <span className="text-muted-foreground line-through text-sm font-mono">
                      Rs.{(originalPrice * quantity).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </span>
                  )}
                </div>
              </div>

              <div className="border-t border-border mb-6" />

              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <span className="block text-[9px] font-mono font-bold uppercase tracking-widest text-muted-foreground">
                    Quantity
                  </span>
                  <span className="mt-1 block text-[10px] font-mono text-slate-400">
                    Select 1–100 units
                  </span>
                </div>
                <div className="flex h-11 items-center border border-[#5A98D8] bg-white">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    disabled={quantity <= 1}
                    onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                    className="flex h-full w-11 items-center justify-center text-[#5A98D8] transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <Minus size={15} />
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={quantity}
                    aria-label="Product quantity"
                    onChange={(event) => {
                      const value = Number(event.target.value);
                      setQuantity(Math.min(100, Math.max(1, Number.isFinite(value) ? value : 1)));
                    }}
                    className="h-full w-16 border-x border-[#5A98D8] bg-white text-center font-mono text-sm font-bold text-[#1a5b9b] outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    disabled={quantity >= 100}
                    onClick={() => setQuantity((value) => Math.min(100, value + 1))}
                    className="flex h-full w-11 items-center justify-center text-[#5A98D8] transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <Plus size={15} />
                  </button>
                </div>
              </div>

              {/* Action Buttons Grid */}
              <div className="flex flex-col sm:flex-row gap-3">
                {isOutOfStock ? (
                  <button
                    disabled
                    className="flex-grow h-11 text-xs uppercase tracking-[0.2em] border rounded-[2px] px-6 font-mono font-bold flex items-center justify-center gap-2.5 opacity-50 bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed w-full"
                  >
                    OUT OF STOCK
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleCartClick('buy')}
                      className="flex-grow h-11 text-xs uppercase tracking-[0.2em] border rounded-[2px] px-6 transition-all hover:opacity-90 active:scale-[0.98] cursor-pointer font-mono font-bold flex items-center justify-center gap-2.5 bg-[#5A98D8] text-white border-[#5A98D8] shadow-md hover:shadow-lg w-full sm:w-auto"
                    >
                      <Zap size={14} className="fill-current" />
                      Buy Now
                    </button>
                    <button
                      onClick={() => handleCartClick('add')}
                      className="flex-grow h-11 text-xs uppercase tracking-[0.2em] border rounded-[2px] px-6 transition-all hover:bg-slate-50 active:scale-[0.98] cursor-pointer font-mono font-bold flex items-center justify-center gap-2.5 border-[#5A98D8] text-[#5A98D8] bg-transparent w-full sm:w-auto"
                    >
                      <ShoppingCart size={14} />
                      {t('addToCart')}
                    </button>
                  </>
                )}
              </div>

              {/* Safety Note */}
              <div className="mt-4 border border-border rounded-[2px] p-3 text-[9px] text-muted-foreground font-mono flex items-start gap-2 bg-muted/30 uppercase tracking-wider">
                <ShieldCheck size={13} className="text-[#5A98D8] shrink-0 mt-0.5" />
                <span>
                  <strong className="text-foreground">No Password Required.</strong> Verify your Player UID on checkout - diamonds &amp; passes credit automatically.
                </span>
              </div>
            </div>

            {/* Description / HTML Content */}
            {currentProduct.description ? (
              <div className="border border-border bg-card p-6 md:p-8 w-full overflow-hidden break-words">
                <h2 className="text-[10px] font-mono font-bold tracking-widest text-muted-foreground uppercase border-b border-border pb-3 mb-4">
                  Product Overview
                </h2>
                <SanitizedHTML
                  html={currentProduct.description}
                  className="prose prose-slate max-w-none text-sm leading-relaxed break-words w-full"
                  style={{ fontFamily: 'inherit' }}
                />
              </div>
            ) : (
              <div className="border border-border bg-card p-6 md:p-8">
                <h2 className="text-[10px] font-mono font-bold tracking-widest text-muted-foreground uppercase border-b border-border pb-3 mb-4">
                  Product Information
                </h2>
                <div className="text-muted-foreground text-[11px] font-mono leading-relaxed space-y-3">
                  <p>
                    Top up your {game} account with {currentProduct.name} on Baasil Topup. Cheapest prices, safe local payments, and super-fast automated delivery in Sri Lanka.
                  </p>
                  <p className="font-bold text-foreground">Why top up at Baasil Topup?</p>
                  <ul className="list-disc pl-5 space-y-1.5">
                    <li>100% Secure — API top-up only requires Player UID. No password needed.</li>
                    <li>Lightning Delivery — Instant 1s fastest delivery in Sri Lanka with automated system.</li>
                    <li>Local Payments — EzCash, Genie QR, LankaQR, card, or bank transfer.</li>
                    <li>24/7 customer support via WhatsApp.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      {/* Floating Cart & Cart Drawer */}
      <span className="cart-wrapper">
        <FloatingCart
          itemCount={cartHook.cartQtyTotal}
          uid={uid}
          validationResult={validationResult}
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
          onOpen={() => setIsCartOpen(true)}
        >
          <CartSidebar
            cart={cart}
            products={products}
            channelLevelUps={[]}
            stockAvailability={stockAvailability}
            feasibility={stockEngine.feasibility}
            subtotal={cartHook.subtotal}
            referralDiscount={cartHook.referralDiscount}
            total={cartHook.total}
            referralBalance={0}
            useReferralBalance={false}
            setUseReferralBalance={() => {}}
            isAuthenticated={auth.isAuthenticated}
            hasMobile={auth.hasMobile}
            validationResult={validationResult}
            isSubmittingOrder={isSubmittingOrder}
            hasOutOfStockItems={cartHook.hasOutOfStockItems}
            outOfStockNames={cartHook.outOfStockNames}
            onClear={clearCart}
            onUpdateQty={updateQty}
            onRemove={removeFromCart}
            onCheckout={handleCheckout}
            isMobile={true}
            paymentConfig={paymentConfig}
            charges={charges}
            activeOffers={activeOffers}
          />
        </FloatingCart>
      </span>

      {/* Modals & Checkouts */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={handleLoginClose}
        onLoginSuccess={handleLoginSuccess}
      />

      <PlayerVerifyModal
        isOpen={isPlayerModalOpen}
        onClose={() => setIsPlayerModalOpen(false)}
        onSuccess={handlePlayerVerifySuccess}
        game={game}
        product={currentProduct}
        hasZoneInput={hasZoneInput}
        playerLabel={playerLabel}
        zoneLabel={zoneLabel}
      />

      <MobileNumberModal
        isOpen={isMobileModalOpen}
        onClose={() => setIsMobileModalOpen(false)}
      />

      {showPaymentModal && (
        <PaymentModal
          show={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          baseAmount={cartHook.totalBeforeCharge}
          charges={charges}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          isSubmittingOrder={isSubmittingOrder}
          onGenieCheckout={handleGenieCheckout}
          onBcoinCheckout={handleBcoinCheckout}
          onEzCashCheckout={handleEzCashCheckout}
          paymentConfig={paymentConfig}
        />
      )}

      {pendingGenieOrder && (
        <QrCodeModal
          isOpen={!!pendingGenieOrder}
          onClose={() => setPendingGenieOrder(null)}
          orderData={pendingGenieOrder}
        />
      )}
    </div>
  );
}
